import { useEffect, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id: string) => void;
    };
    __turnstileOnLoad?: () => void;
  }
}

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__turnstileOnLoad&render=explicit";

let scriptLoading: Promise<void> | null = null;
const loadTurnstile = (): Promise<void> => {
  if (typeof window === "undefined" || window.turnstile) {
    return Promise.resolve();
  }
  scriptLoading ??= new Promise<void>((resolve) => {
    window.__turnstileOnLoad = () => resolve();
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  });
  return scriptLoading;
};

/** Tokens are single-use — call after every submit so the widget issues a fresh one. */
export const resetTurnstile = () => {
  if (typeof window !== "undefined") {
    window.turnstile?.reset();
  }
};

export type TurnstileProps = {
  onToken: (token: string | null) => void;
  className?: string;
  /** "interaction-only" (default) stays invisible unless Cloudflare needs a click;
   *  "always" shows the checkbox box up front — used when a form must make the
   *  verification unmissable (the newsletter after a submit without a token). */
  appearance?: "interaction-only" | "always";
};

/**
 * Cloudflare Turnstile (managed mode). With `interaction-only` appearance the
 * widget stays invisible while Cloudflare validates silently and only
 * materializes when it decides a visitor must interact. Unconfigured site key
 * (no NEXT_PUBLIC_TURNSTILE_SITE_KEY) renders nothing — the server side skips
 * verification in that case too, so dev without keys keeps working.
 */
export default function Turnstile(props: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(props.onToken);
  onTokenRef.current = props.onToken;

  useEffect(() => {
    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
    if (!siteKey) {
      return;
    }
    let cancelled = false;
    loadTurnstile().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) {
        return;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        appearance: props.appearance ?? "interaction-only",
        "refresh-expired": "auto",
        callback: (token: string) => onTokenRef.current(token),
        "expired-callback": () => onTokenRef.current(null),
        "error-callback": () => onTokenRef.current(null),
      });
    });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={containerRef} className={props.className ?? ""} />;
}
